
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { AdminSearch } from "../shared/AdminSearch";
import { AdminFilters } from "../shared/AdminFilters";
import { AdminPagination } from "../shared/AdminPagination";
import { CategoryEditDialog } from "./CategoryEditDialog";
import { CategoryCard } from "./CategoryCard";
import { useCategories } from "./useCategories";
import { useCategoriesManager } from "./useCategoriesManager";
import { EpisodeCategory } from "../episodes/types";

export const EpisodesCategoriesSection = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<EpisodeCategory | null>(null);

  const { categories, loading, saveCategory, deleteCategory } = useCategories();
  const {
    filteredCategories,
    pagination,
  } = useCategoriesManager({ 
    categories, 
    searchTerm, 
    filters, 
    saveCategory 
  });

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    pagination.resetPagination();
  };

  const handleResetFilters = () => {
    setFilters({ status: "" });
    setSearchTerm("");
    pagination.resetPagination();
  };

  const handleEdit = (category: EpisodeCategory) => {
    setEditingCategory(category);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingCategory(null);
    setDialogOpen(true);
  };

  const handleSave = async (categoryData: any) => {
    await saveCategory(categoryData, editingCategory?.id);
    setEditingCategory(null);
  };

  const handleDelete = async (categoryId: string) => {
    await deleteCategory(categoryId);
  };

  if (loading) {
    return <div className="text-center">Loading episodes categories...</div>;
  }

  const hasFilters = Boolean(searchTerm || Object.values(filters).some(v => v));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">Episodes Categories</h3>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <AdminSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search episodes categories..."
          className="max-w-md"
        />
        
        <AdminFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
        />
      </div>

      <CategoryEditDialog
        category={editingCategory}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
      />

      <div className="grid gap-4">
        {pagination.paginatedItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {hasFilters 
              ? "No episodes categories match your search criteria." 
              : "No episodes categories found. Click 'Add Category' to create your first category."
            }
          </div>
        ) : (
          pagination.paginatedItems.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      <AdminPagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        itemsPerPage={10}
        totalItems={filteredCategories.length}
        onPageChange={pagination.goToPage}
        onItemsPerPageChange={() => {}} // Fixed items per page for now
      />
    </div>
  );
};
